import { css } from 'astroturf';
import Prism from 'prismjs';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const styles = css`
  .dialog {
    overscroll-behavior: contain;
    width: min(1000px, calc(100vw - 1rem));
    max-height: calc(100svh - 2rem);
    border: 0;
    border-radius: 8px;
    padding: 0;
    background: #fff;
    color: #0a1530;
    box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.024),
      0 1px 0 rgba(0, 0, 0, 0.05),
      0 0 8px rgba(0, 0, 0, 0.03),
      0 20px 30px rgba(0, 0, 0, 0.1);
    opacity: 0;
    scale: 0.95;

    @media (prefers-reduced-motion: no-preference) {
      transition:
        opacity 0.15s ease-in-out,
        scale 0.15s ease-in-out,
        overlay 0.15s ease-in-out,
        display 0.15s ease-in-out;
      transition-behavior: allow-discrete;
    }

    &[open] {
      scale: 1;
      opacity: 1;
    }

    @starting-style {
      &[open] {
        scale: 0.95;
        opacity: 0;
      }
    }

    &::backdrop {
      overscroll-behavior: contain;
      overflow: auto;
      background: rgba(10, 21, 48, 0);
      backdrop-filter: blur(0px);

      @media (prefers-reduced-motion: no-preference) {
        transition:
          background-color 0.15s ease-in-out,
          backdrop-filter 0.15s ease-in-out,
          overlay 0.15s ease-in-out,
          display 0.15s ease-in-out;
        transition-behavior: allow-discrete;
      }
    }

    &[open]::backdrop {
      background: rgba(10, 21, 48, 0.55);
      backdrop-filter: blur(2px);
    }

    @starting-style {
      &[open]::backdrop {
        background: rgba(10, 21, 48, 0);
        backdrop-filter: blur(0px);
      }
    }

    h4 {
      margin: 0 0 0.55rem;
      font-size: 0.95rem;
      font-weight: 700;
      color: #0a1530;
    }
  }

  .close {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    border: 0;
    background: #0000;
    color: #39728d;
    width: 2rem;
    height: 2rem;
    border-radius: 4px;
    font-size: 1.2rem;
    line-height: 1;
    z-index: 1;

    &:hover,
    &:focus-visible {
      color: #0a1530;
      background: #0a153011;
    }
  }

  .body {
    padding: 0;
    display: grid;
  }

  @media (max-width: 50rem) {
    .dialog {
      width: calc(100vw - 1rem);
      max-height: calc(100svh - 1rem);
    }
  }
`;

export default function InfoDialog({ id, triggerClassName, triggerLabel, triggerContent, renderTrigger, children, isOpen }) {
  const dialogRef = useRef(null);
  // Only mount children once the dialog has been requested to open.
  // Initialise to true if isOpen starts as true (URL-param-driven open on load).
  const [contentReady, setContentReady] = useState(() => isOpen === true);
  // Tracks a pending open requested via the trigger button before content was mounted.
  const pendingOpenRef = useRef(false);

  // URL-driven open: when isOpen becomes true, mount content if not already, then show.
  useEffect(() => {
    const dialog = dialogRef.current;

    if (!dialog || typeof isOpen !== 'boolean') {
      return;
    }

    if (isOpen && !dialog.open) {
      if (!contentReady) {
        setContentReady(true);
        // showModal will be called in the effect below once contentReady flips.
      } else {
        dialog.showModal();
        Prism.highlightAllUnder(dialog);
      }
    }

    if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen, contentReady]);

  // After content mounts due to a direct button-click, fulfil the pending open.
  useEffect(() => {
    if (contentReady && pendingOpenRef.current) {
      pendingOpenRef.current = false;
      const dialog = dialogRef.current;
      if (dialog && !dialog.open) {
        dialog.showModal();
        Prism.highlightAllUnder(dialog);
      }
    }
  }, [contentReady]);

  const show = () => {
    if (!contentReady) {
      pendingOpenRef.current = true;
      setContentReady(true);
    } else {
      const dialog = dialogRef.current;
      if (dialog && !dialog.open) {
        dialog.showModal();
        Prism.highlightAllUnder(dialog);
      }
    }
  };

  const close = () => {
    const dialog = dialogRef.current;

    if (dialog && dialog.open) {
      dialog.close();
    }
  };

  const dialogElement = (
    <dialog closedby="any" id={id} ref={dialogRef} className={styles.dialog}>
      <div className={styles.body}>
        <button className={styles.close} type="button" aria-label="Close" onClick={close}>
          ×
        </button>
        {contentReady ? children : null}
      </div>
    </dialog>
  );

  return (
    <>
      {renderTrigger ? renderTrigger(show) : (
        <button
          className={triggerClassName}
          type="button"
          aria-label={triggerLabel}
          data-tip={triggerLabel}
          onClick={show}
        >
          {triggerContent}
        </button>
      )}
      {typeof document !== 'undefined' ? createPortal(dialogElement, document.body) : null}
    </>
  );
}
